import {
  RELEASE_FLAG_DESCRIPTIONS,
  RELEASE_FLAG_LABELS,
} from '@frontend-shared/release-flags/descriptions';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReleaseFlagType } from '@/entities/session/model/releaseFlag';
import { getSessionStorage, setSessionStorage } from '@/shared/lib/storage';
import { Dialog, Select, Separator, Text } from '@/shared/ui';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const RELEASE_FLAG_TYPES = (Object.keys(RELEASE_FLAG_LABELS) as ReleaseFlagType[]).map((type) => ({
  releaseFlagType: type,
  label: RELEASE_FLAG_LABELS[type],
  description: RELEASE_FLAG_DESCRIPTIONS[type],
}));

const DEFAULT_VALUE = 'default';
const ENABLED_VALUE = 'enabled';
const DISABLED_VALUE = 'disabled';
type SelectValue = typeof DEFAULT_VALUE | typeof ENABLED_VALUE | typeof DISABLED_VALUE;

const FLAG_OPTIONS: { value: SelectValue; label: string }[] = [
  { value: DEFAULT_VALUE, label: 'デフォルト（API準拠）' },
  { value: ENABLED_VALUE, label: '有効（enabled）' },
  { value: DISABLED_VALUE, label: '無効（disabled）' },
];

const toSelectValue = (override: boolean | undefined): SelectValue => {
  if (override === undefined) return DEFAULT_VALUE;
  return override ? ENABLED_VALUE : DISABLED_VALUE;
};

export const ReleaseFlagControlDialog: FC<Props> = ({ open, onOpenChange }) => {
  const { t } = useTranslation();

  const [overrides, setOverrides] = useState<Record<string, boolean>>(() =>
    getSessionStorage('debug-release-flags'),
  );

  const handleChange = ({
    releaseFlagType,
    value,
  }: {
    releaseFlagType: ReleaseFlagType;
    value: SelectValue;
  }) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (value === DEFAULT_VALUE) {
        delete next[releaseFlagType];
      } else {
        next[releaseFlagType] = value === ENABLED_VALUE;
      }
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={() => {
        setSessionStorage('debug-release-flags', overrides);
        window.location.reload();
        onOpenChange(false);
      }}
      confirmLabel={t('debug-menu.apply')}
      title={t('debug-menu.release-flag-control')}
    >
      <div className="space-y-4">
        <div>
          <Text variant="label">{t('debug-menu.session-storage-note')}</Text>
        </div>

        <Separator />

        <div className="space-y-2">
          {RELEASE_FLAG_TYPES.map(({ releaseFlagType, label, description }) => (
            <div key={releaseFlagType} className="flex items-center justify-between gap-2">
              <div className="max-w-[60%]">
                <Text variant="label" className="shrink-0">
                  {label}
                </Text>
                <Text variant="description">{description}</Text>
              </div>
              <Select
                rootClassName="w-[200px]"
                value={toSelectValue(overrides[releaseFlagType])}
                onValueChange={(value) =>
                  handleChange({ releaseFlagType, value: value as SelectValue })
                }
                options={FLAG_OPTIONS}
              />
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
};
